/**
 * =============================================================================
 * TemplateExecutionsTab — aba "Execuções" da página Templates do Sistema
 *
 * Estrutura espelhada em `CustomerListsTab` (Central de Importação de Listas):
 * situação em bolinha colorida + palavra, andamento em texto (com barra fina
 * apenas enquanto roda), ações no menu de três pontinhos e paginador.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-08 v1.0.0 - Versão inicial (padronização com Importação de Listas).
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Download,
  History,
  MoreHorizontal,
  PlayCircle,
  RefreshCw,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PaginationFooter } from '@/components/ui/pagination-footer';

import { useTemplateJobs } from '@/hooks/useSystemTemplates';

/** Cor da bolinha de situação (mesmos tokens da Importação de Listas). */
const STATUS_DOT_COLORS: Record<string, string> = {
  pending: 'bg-muted-foreground',
  running: 'bg-blue-500',
  paused: 'bg-amber-500',
  completed: 'bg-green-500',
  failed: 'bg-destructive',
  compensating: 'bg-amber-500',
  compensated: 'bg-muted-foreground',
};

/** Rótulos amigáveis das situações. */
const STATUS_LABELS: Record<string, string> = {
  pending: 'Na fila',
  running: 'Em andamento',
  paused: 'Pausado',
  completed: 'Concluído',
  failed: 'Com erro',
  compensating: 'Desfazendo',
  compensated: 'Desfeito',
};

/** Rótulos amigáveis dos tipos de execução. */
const KIND_LABELS: Record<string, string> = {
  export: 'Geração do arquivo',
  import: 'Conferência do arquivo',
  apply: 'Aplicação nesta conta',
  rollback: 'Reversão',
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface TemplateExecutionsTabProps {
  onDetails: (jobId: string) => void;
  onReview: (versionId: string) => void;
  onDownload: (versionId: string) => void;
  onRetry: (jobId: string) => void;
  /** Contingência: processa a fila na hora. */
  onProcessQueue: () => void;
  processing?: boolean;
  downloading?: boolean;
  retrying?: boolean;
}

export function TemplateExecutionsTab({
  onDetails,
  onReview,
  onDownload,
  onRetry,
  onProcessQueue,
  processing,
  downloading,
  retrying,
}: TemplateExecutionsTabProps) {
  const { data: execucoes = [], isLoading, isFetching, refetch } = useTemplateJobs(50);

  const [buscaInput, setBuscaInput] = useState('');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => {
      setBusca(buscaInput.trim().toLowerCase());
      setPagina(1);
    }, 300);
    return () => clearTimeout(t);
  }, [buscaInput]);

  const filtrados = useMemo(() => {
    if (!busca) return execucoes;
    return execucoes.filter(
      (j) =>
        (j.package_name ?? '').toLowerCase().includes(busca) ||
        (KIND_LABELS[j.kind] ?? j.kind).toLowerCase().includes(busca),
    );
  }, [execucoes, busca]);

  const visiveis = useMemo(
    () => filtrados.slice((pagina - 1) * tamanhoPagina, pagina * tamanhoPagina),
    [filtrados, pagina, tamanhoPagina],
  );

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b bg-muted/30">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar por template ou tipo..."
              value={buscaInput}
              onChange={(e) => setBuscaInput(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
          {buscaInput && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setBuscaInput('')}
              className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          )}
          {isFetching && (
            <span className="text-xs text-muted-foreground animate-pulse">Atualizando...</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="xs" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button variant="outline" size="xs" onClick={onProcessQueue} disabled={processing}>
            <PlayCircle className="mr-1.5" />
            {processing ? 'Processando...' : 'Processar fila'}
          </Button>
        </div>
      </div>

      <div className="p-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Execuções recentes
            </CardTitle>
            <CardDescription>
              O processamento começa sozinho ao gerar, conferir ou aplicar um template
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : filtrados.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Nenhuma execução encontrada</p>
                <p className="text-sm">
                  {busca
                    ? 'Ajuste a busca ou limpe o filtro.'
                    : 'Gere ou importe um template para ver o andamento aqui.'}
                </p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead className="w-40">Andamento</TableHead>
                      <TableHead>Início</TableHead>
                      <TableHead className="w-16 text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visiveis.map((execucao) => {
                      const emAndamento =
                        execucao.status === 'running' || execucao.status === 'compensating';
                      const podeRevisar =
                        execucao.kind === 'import' &&
                        execucao.status === 'completed' &&
                        !!execucao.package_version_id;
                      const podeBaixar =
                        execucao.kind === 'export' &&
                        execucao.status === 'completed' &&
                        !!execucao.package_version_id;
                      const podeReprocessar = execucao.status === 'failed';

                      return (
                        <TableRow key={execucao.id}>
                          <TableCell className="font-medium">
                            {KIND_LABELS[execucao.kind] ?? execucao.kind}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {execucao.package_name ?? '—'}
                            {execucao.version_number ? ` (v${execucao.version_number})` : ''}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm text-foreground">
                              <span
                                className={cn(
                                  'h-2 w-2 rounded-full',
                                  STATUS_DOT_COLORS[execucao.status] || 'bg-muted-foreground',
                                )}
                              />
                              {STATUS_LABELS[execucao.status] ?? execucao.status}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <span className="text-sm tabular-nums text-muted-foreground">
                                {execucao.progress_percent ?? 0}%
                              </span>
                              {/* Barra fina apenas enquanto a execução está rodando */}
                              {emAndamento && (
                                <Progress
                                  value={execucao.progress_percent}
                                  className="h-1.5 bg-muted"
                                />
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(execucao.created_at).toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                  <span className="sr-only">Ações</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem onClick={() => onDetails(execucao.id)}>
                                  <Activity className="h-4 w-4 mr-2" />
                                  Ver detalhes
                                </DropdownMenuItem>
                                {podeRevisar && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      onReview(execucao.package_version_id as string)
                                    }
                                  >
                                    <PlayCircle className="h-4 w-4 mr-2" />
                                    Revisar e aplicar
                                  </DropdownMenuItem>
                                )}
                                {podeBaixar && (
                                  <DropdownMenuItem
                                    disabled={downloading}
                                    onClick={() =>
                                      onDownload(execucao.package_version_id as string)
                                    }
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Baixar arquivo
                                  </DropdownMenuItem>
                                )}
                                {podeReprocessar && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      disabled={retrying}
                                      onClick={() => onRetry(execucao.id)}
                                    >
                                      <RotateCcw className="h-4 w-4 mr-2" />
                                      Tentar de novo
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                <div className="border-t mt-2 px-4 py-2 bg-background">
                  <PaginationFooter
                    totalItems={filtrados.length}
                    currentPage={pagina}
                    pageSize={tamanhoPagina}
                    onPageChange={setPagina}
                    onPageSizeChange={(tamanho) => {
                      setTamanhoPagina(tamanho);
                      setPagina(1);
                    }}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    borderClassName=""
                    className="mt-0 pt-0"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export default TemplateExecutionsTab;
